import React, { useMemo, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControlLabel,
  Link,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import PageHeader from '../../components/system/PageHeader'
import DisabledReason from '../../components/system/DisabledReason'
import { BRAND_GRADIENT } from '../../components/social-media/design-tokens'
import { refuse, NOBODY_KNOWN } from '../../utils/permissionReason'
import useRouter from '../../hooks/useRouter'
import {
  useFetchFreshReconciliation,
  useSetCountingSettings,
  useSkyReconciliation,
  ReconRequestError,
  type CommentDay,
  type ReconBridge,
  type ReconCell,
  type ReconMeasures,
  type ReconPost,
  type ReconStatus,
} from '../../hooks/socialdash/useSkyReconciliation'
import ProductTabs, { RECONCILIATION_TAB } from './ProductTabs'
import AdminViewTabs from './AdminViewTabs'
import RangeControls from './reconciliation/RangeControls'
import CommentsSection from './reconciliation/CommentsSection'
import CommentDayDrawer from './reconciliation/CommentDayDrawer'
import VerdictStrip from './reconciliation/VerdictStrip'
import PostsLedger from './reconciliation/PostsLedger'
import AddedByPlatinum from './reconciliation/AddedByPlatinum'
import {
  addedOnTop,
  commentsToImport,
  bridgeFor,
  campaignLabel,
  cellReasons,
  diffLabel,
  figure,
  fmtInt,
  monthLabel,
  oursFor,
  platformLabel,
  postsByReason,
  provenanceLine,
  readReconQuery,
  reasonLabel,
  reconQueryParams,
  residualNote,
  safeHref,
  signed,
  unexplainedColor,
  utcDateTime,
  utcDayLabel,
  verdictLine,
  type ReconQuery,
} from './reconciliationModel'

/**
 * Reconciliation — Sky's own Sprinklr numbers beside Platinum's, for any range
 * of UTC days, with the reason for every difference (Kai, 16 Oct 2026:
 * "anybody can see why the numbers are the way they are").
 *
 * Asked on demand (design/2026-10-19-sky-reconciliation-on-demand.md): a person
 * picks a UTC date range and platforms (kept in the URL, so a link reproduces
 * the view); the server asks Sprinklr live and keeps Sky's answer for an hour.
 *
 * Laid out for confidence (T19b, design/2026-10-19-sky-reconciliation-page-redesign.md):
 * the verdict strip first (one sentence, comments, posts, what Platinum adds,
 * where Sprinklr was read), then the comments ledger and the posts ledger, then
 * what Platinum adds on top. The working (step-by-step sum, by month, every
 * post by reason) and the Managers' counting settings are collapsed below.
 * Nothing is red except posts that need a look beyond the tolerance. The full
 * explainer for Sky is docs/sky-sprinklr-numbers.md.
 */

const SKY_TABLE = "Sky's Sprinklr table"

/** The page's sentence for a failed read, by the server's status. */
function errorTitle(err: unknown): string {
  const status = err instanceof ReconRequestError ? err.status : undefined
  if (status === 400 || status === 422) return 'Choose a different range'
  if (status === 429) return 'Sprinklr is busy for this page'
  if (status === 503) return 'Sprinklr cannot be asked'
  if (status === 502) return 'Sprinklr did not answer'
  return 'The comparison could not be made'
}

const num = { fontVariantNumeric: 'tabular-nums' } as const

const BridgeCells: React.FC<{ m: ReconMeasures; sign?: boolean; bold?: boolean }> = ({ m, sign, bold }) => (
  <>
    {(['posts', 'reach', 'views', 'engagements'] as const).map((k) => (
      <TableCell key={k} align="right" sx={{ ...num, fontWeight: bold ? 600 : undefined, whiteSpace: 'nowrap' }}>
        {sign ? signed(m[k]) : fmtInt(m[k])}
      </TableCell>
    ))}
  </>
)

/**
 * The step-by-step sum: Platinum's dashboard number, one line per known
 * reason, "Not explained", then Sprinklr's number. Computed and tested in
 * goapi/pkg/sprinklrrecon (bridge.go); it adds up exactly by construction.
 */
const BridgeTable: React.FC<{ bridge: ReconBridge; status: ReconStatus }> = ({ bridge, status }) => {
  const ne = bridge.notExplained
  const neColor = ne.count > 0 ? `${unexplainedColor(status)}.main` : 'text.secondary'
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }} gutterBottom>Step by step, from Platinum to Sprinklr</Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" data-testid="recon-bridge">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell align="right">Posts</TableCell>
              <TableCell align="right">Reach</TableCell>
              <TableCell align="right">Views</TableCell>
              <TableCell align="right">Engagements</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>Platinum, as the dashboards count</TableCell>
              <BridgeCells m={bridge.platinum} bold />
            </TableRow>
            {bridge.steps.map((s) => (
              <TableRow key={s.reason}>
                <TableCell component="th" scope="row" sx={{ minWidth: 320 }}>
                  <Typography variant="body2">
                    {reasonLabel(s.reason)}
                    <Typography component="span" variant="body2" color="text.secondary"> · {fmtInt(s.count)} post{s.count === 1 ? '' : 's'}</Typography>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{s.explanation}</Typography>
                </TableCell>
                <BridgeCells m={s.amount} sign />
              </TableRow>
            ))}
            <TableRow>
              <TableCell component="th" scope="row">
                <Typography variant="body2" color={neColor}>
                  {reasonLabel('unexplained')}
                  <Typography component="span" variant="body2" color="text.secondary"> · {fmtInt(ne.count)} post{ne.count === 1 ? '' : 's'}</Typography>
                </Typography>
                {ne.count > 0 && <Typography variant="caption" color="text.secondary">{ne.explanation}</Typography>}
              </TableCell>
              <BridgeCells m={ne.amount} sign />
            </TableRow>
            <TableRow sx={{ '& > *': { borderTop: 2, borderTopColor: 'divider' } }}>
              <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>Sprinklr, Sky's dashboard added up</TableCell>
              <BridgeCells m={bridge.sprinklr} bold />
            </TableRow>
          </TableBody>
        </Table>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Each line is the total of the posts listed under its reason below. Sprinklr includes the platforms Platinum
        doesn&apos;t cover. The sum follows the counting settings in force.
      </Typography>
    </Paper>
  )
}

const skyOurs = (sky: number, ours: number, hasSky: boolean) => (hasSky ? `${fmtInt(sky)} / ${fmtInt(ours)}` : `— / ${fmtInt(ours)}`)

const PostTable: React.FC<{ posts: ReconPost[]; showDetail?: boolean }> = ({ posts, showDetail }) => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell>Month</TableCell>
        <TableCell>Campaign</TableCell>
        <TableCell>Platform</TableCell>
        <TableCell>Post</TableCell>
        <TableCell align="right">Rows, Sky / ours</TableCell>
        <TableCell align="right">Reach, Sky / Platinum</TableCell>
        <TableCell align="right">Views, Sky / Platinum</TableCell>
        <TableCell align="right">Engagements, Sky / Platinum</TableCell>
        {showDetail && <TableCell>What is wrong</TableCell>}
      </TableRow>
    </TableHead>
    <TableBody>
      {posts.map((p) => {
        const hasSky = p.skyRows > 0
        const href = safeHref(p.url)
        return (
          <TableRow key={`${p.reason}-${p.key}-${p.explanation}`}>
            <TableCell>{monthLabel(p.month)}</TableCell>
            <TableCell>{campaignLabel(p.campaign, p.campaignId)}</TableCell>
            <TableCell>{platformLabel(p.platform)}</TableCell>
            <TableCell sx={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {href ? (
                <Link href={href} target="_blank" rel="noopener noreferrer" color="info.main">{href}</Link>
              ) : (
                p.url || p.key
              )}
            </TableCell>
            <TableCell align="right">{hasSky ? fmtInt(p.skyRows) : '—'} / {p.ourTableRows ? fmtInt(p.ourTableRows) : '—'}</TableCell>
            <TableCell align="right">{skyOurs(p.skyReach, p.ourReach, hasSky)}</TableCell>
            <TableCell align="right">{skyOurs(p.skyViews, p.ourViews, hasSky)}</TableCell>
            <TableCell align="right">{skyOurs(p.skyEngagements, p.ourEngagements, hasSky)}</TableCell>
            {showDetail && <TableCell sx={{ minWidth: 280 }}>{p.explanation}</TableCell>}
          </TableRow>
        )
      })}
    </TableBody>
  </Table>
)

export const ReconciliationPage: React.FC = () => {
  const router = useRouter()
  // The URL is the state: a link reproduces the view. Read once per render;
  // the default range follows the clock, so a link should carry its dates.
  const query = readReconQuery(router.params, new Date())
  const params = reconQueryParams(query)
  const paramsKey = `${params.from}|${params.to}|${params.platforms ?? ''}`
  // Nothing is asked of Sprinklr until Apply, even when the URL carries a
  // range: each read spends the hourly Sprinklr allowance the import shares.
  const [applied, setApplied] = useState(false)
  const { data, isLoading, isFetching, error } = useSkyReconciliation(params, applied)
  const fresh = useFetchFreshReconciliation(params)
  const setSettings = useSetCountingSettings()
  const [onlyDiffering, setOnlyDiffering] = useState(true)
  const [checkDay, setCheckDay] = useState<CommentDay | null>(null)

  const apply = (q: ReconQuery) => {
    setApplied(true)
    router.setParams(reconQueryParams(q), true)
  }

  const settings = data?.settings
  const posts = settings?.postsInSprinklrTable ?? false
  const asTable = settings?.countAsSprinklrTable ?? false
  const report = data?.posts ?? null

  const cells = useMemo(() => {
    const all = (report?.cells ?? []).filter((c) => c.imported)
    if (!onlyDiffering) return all
    return all.filter((c) => {
      const o = oursFor(c, posts, asTable)
      return (
        c.reasons ||
        !c.explained ||
        o.posts !== c.sky.posts ||
        Math.abs(o.reach - c.sky.reach) >= 1 ||
        Math.abs(o.views - c.sky.views) >= 1 ||
        Math.abs(o.engagements - c.sky.engagements) >= 1
      )
    })
  }, [report, onlyDiffering, posts, asTable])

  const groups = useMemo(() => postsByReason(report?.posts ?? []), [report])
  const bridge = bridgeFor(report?.bridges, posts, asTable)
  const status: ReconStatus = report?.status ?? (report?.unexplained ? 'unexplained' : 'match')
  const flagColor = unexplainedColor(status)
  const switchReason = data && !data.canEdit
    ? refuse("You can't change how posts are counted.", NOBODY_KNOWN).reason
    : undefined

  const flip = (next: { postsInSprinklrTable: boolean; countAsSprinklrTable: boolean }) => setSettings.mutate(next)

  // One Platinum number per measure: the strip and the posts ledger show
  // Platinum on Sky's terms (bridge.matched); the working shows the dashboards'
  // own number (bridge.platinum), labelled as such.
  const skyPosts: ReconMeasures | null = bridge ? bridge.sprinklr : report ? report.totals.sky : null
  const ourPosts: ReconMeasures | null = bridge ? bridge.matched : report ? oursFor(report.totals, posts, asTable) : null
  const added = data ? addedOnTop(data.comments) : null
  const toImport = data ? commentsToImport(data.comments, data.frontierMs) : null

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', bgcolor: 'background.default' }}>
      <ProductTabs product={RECONCILIATION_TAB} />
      {/* position: relative makes this box the containing block of the
          switches' visually hidden labels (position: absolute); without it
          they hang below the window and the whole page scrolls as well. */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', overflowX: 'hidden', position: 'relative' }}>
        <PageHeader
          title="Reconciliation"
          gradient={BRAND_GRADIENT}
          meta={
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Sky&apos;s Sprinklr numbers beside Platinum&apos;s, for any range of days.
            </Typography>
          }
        />
        <AdminViewTabs view="reconciliation" />
        <Box sx={{ px: { xs: 2, sm: 3 }, py: 3, display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 1400, minWidth: 0 }}>
          <RangeControls key={paramsKey} query={query} onApply={apply} busy={isFetching} />
          {!applied && (
            <Typography variant="body2" color="text.secondary" data-testid="recon-not-applied">
              Choose the days and platforms, then press Apply to ask Sprinklr.
            </Typography>
          )}
          {isLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary">
                Asking Sprinklr for {utcDayLabel(Date.parse(`${query.from}T00:00:00Z`))} to {utcDayLabel(Date.parse(`${query.to}T00:00:00Z`))}. A new range can take up to two minutes.
              </Typography>
            </Box>
          )}
          {error && (
            <Alert severity="error" data-testid="recon-error">
              <Typography variant="body1" sx={{ fontWeight: 600 }}>{errorTitle(error)}</Typography>
              <Typography variant="body2">{(error as Error).message}</Typography>
            </Alert>
          )}
          {fresh.error && <Alert severity="error">{(fresh.error as Error).message}</Alert>}

          {report && data && skyPosts && ourPosts && added && toImport && (
            <>
              <VerdictStrip
                verdict={verdictLine(report, data.window)}
                comments={figure(toImport.sky, toImport.ours)}
                commentsNote={toImport.laterInSprinklr > 0
                  ? `Days to ${utcDayLabel(toImport.lastFullDayMs)}, Platinum's last full day. Sprinklr lists ${fmtInt(toImport.laterInSprinklr)} on later days; they are compared once Platinum imports them.`
                  : undefined}
                posts={figure(skyPosts.posts, ourPosts.posts)}
                added={{ coded: fmtInt(added.coded), x: fmtInt(added.x), hasX: added.x > 0 }}
                provenance={provenanceLine(data)}
                partial={data.partial}
                canReadAgain={data.canFetchFresh}
                onReadAgain={() => fresh.mutate()}
                readingAgain={fresh.isPending}
                busy={isFetching}
              />

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 7fr) minmax(0, 5fr)' }, gap: 3, alignItems: 'start' }}>
                <CommentsSection
                  report={data.comments}
                  window={data.window}
                  platforms={query.platforms.length}
                  frontierMs={data.frontierMs}
                  onCheckDay={setCheckDay}
                />
                <PostsLedger
                  sprinklr={skyPosts}
                  platinum={ourPosts}
                  steps={bridge?.steps ?? []}
                  dashboards={bridge?.platinum}
                  notExplained={bridge?.notExplained}
                  needsALook={report.posts.filter((p) => p.reason === 'unexplained')}
                  status={status}
                />
              </Box>

              <AddedByPlatinum comments={data.comments} onOpenDashboards={router.navigate ? () => router.navigate('sky-social-media') : undefined} />

              <Accordion variant="outlined" disableGutters slotProps={{ transition: { unmountOnExit: true } }} data-testid="recon-working">
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>Show the working</Typography>
                    <Typography variant="body2" color="text.secondary">
                      The step-by-step sum, by month, campaign and platform, and every post by reason.
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                  {bridge && <BridgeTable bridge={bridge} status={status} />}

                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>By month, campaign and platform</Typography>
                      <FormControlLabel
                        control={<Switch color="info" checked={onlyDiffering} onChange={(_, v) => setOnlyDiffering(v)} />}
                        label="Only rows that differ"
                      />
                    </Box>
                    <Box sx={{ overflowX: 'auto' }}>
                      <Table size="small" data-testid="recon-cells">
                        <TableHead>
                          <TableRow>
                            <TableCell>Month</TableCell>
                            <TableCell>Campaign</TableCell>
                            <TableCell>Platform</TableCell>
                            <TableCell align="right">Posts, Sprinklr</TableCell>
                            <TableCell align="right">Posts, Platinum</TableCell>
                            <TableCell align="right">Reach, Sprinklr</TableCell>
                            <TableCell align="right">Reach, Platinum</TableCell>
                            <TableCell align="right">Views, Sprinklr</TableCell>
                            <TableCell align="right">Views, Platinum</TableCell>
                            <TableCell align="right">Engagements, Sprinklr</TableCell>
                            <TableCell align="right">Engagements, Platinum</TableCell>
                            <TableCell>Why they differ</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {cells.map((c: ReconCell) => {
                            const o = oursFor(c, posts, asTable)
                            return (
                              <TableRow key={`${c.month}|${c.campaignId}|${c.platform}`} sx={c.explained || status !== 'unexplained' ? undefined : { bgcolor: 'error.light' }}>
                                <TableCell>{monthLabel(c.month)}</TableCell>
                                <TableCell>{campaignLabel(c.campaign, c.campaignId)}</TableCell>
                                <TableCell>{platformLabel(c.platform)}</TableCell>
                                <TableCell align="right" sx={num}>{fmtInt(c.sky.posts)}</TableCell>
                                <TableCell align="right" sx={num}>{fmtInt(o.posts)}</TableCell>
                                <TableCell align="right" sx={num}>{fmtInt(c.sky.reach)}</TableCell>
                                <TableCell align="right" sx={num}>
                                  {fmtInt(o.reach)}
                                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>{diffLabel(o.reach, c.sky.reach)}</Typography>
                                </TableCell>
                                <TableCell align="right" sx={num}>{fmtInt(c.sky.views)}</TableCell>
                                <TableCell align="right" sx={num}>
                                  {fmtInt(o.views)}
                                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>{diffLabel(o.views, c.sky.views)}</Typography>
                                </TableCell>
                                <TableCell align="right" sx={num}>{fmtInt(c.sky.engagements)}</TableCell>
                                <TableCell align="right" sx={num}>
                                  {fmtInt(o.engagements)}
                                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>{diffLabel(o.engagements, c.sky.engagements)}</Typography>
                                </TableCell>
                                <TableCell>
                                  {residualNote(c) && <Chip label={residualNote(c)} size="small" color={flagColor} sx={{ mr: 0.5, mb: 0.5 }} />}
                                  {cellReasons(c).map((r) => (
                                    <Chip key={r} label={r} size="small" color={r.startsWith(reasonLabel('unexplained')) ? flagColor : 'default'} sx={{ mr: 0.5, mb: 0.5 }} />
                                  ))}
                                </TableCell>
                              </TableRow>
                            )
                          })}
                          {cells.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={12}>
                                <Typography variant="body2" color="text.secondary">Every row matches Sky&apos;s dashboard.</Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </Box>
                  </Paper>

                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }} gutterBottom>Every post behind the sum, by reason</Typography>
                    {groups.map((g) => (
                      <Accordion key={g.reason} disableGutters slotProps={{ transition: { unmountOnExit: true } }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box>
                            <Typography sx={{ fontWeight: 600 }} color={g.reason === 'unexplained' ? `${flagColor}.main` : undefined}>
                              {reasonLabel(g.reason)} ({fmtInt(g.posts.length)})
                            </Typography>
                            <Typography variant="body2" color="text.secondary">{g.posts[0]?.explanation}</Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ overflowX: 'auto' }}>
                          <PostTable posts={g.posts} showDetail={g.reason === 'unexplained' || g.reason === 'moved_since_read'} />
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Box>

                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }} gutterBottom>How the numbers work</Typography>
                    <Typography variant="body2" component="div" color="text.secondary">
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        <li>Platinum replays Sky&apos;s own Sprinklr dashboard, so the rules for which posts and comments count are Sky&apos;s.</li>
                        <li>One extra campaign, EB&amp;A – Corporate Comms, is read on top of Sky&apos;s list, as agreed with Sky on 11 Aug 2026.</li>
                        <li>Every day and month here is a UTC one: a post by its published date, a comment by its own date. Sprinklr is asked in UTC too, so both sides cut the days at the same moment, as the dashboards do.</li>
                        <li>Comments are counted whether or not their text was coded; the dashboards&apos; comment figures count coded comments only.</li>
                        <li>On X, replies and mentions sit on the Sky tweet they answer; reposts sit on Sky&apos;s account.</li>
                        <li>Reach, views and engagements are added up over posts; engagement rates are averaged, not added.</li>
                        <li>Sprinklr shows comments, likes and shares as -1 under Sky&apos;s &quot;posts only&quot; filter; Platinum reads them without that one filter and never stores a -1.</li>
                      </ul>
                    </Typography>
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion variant="outlined" disableGutters data-testid="recon-settings">
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>Settings (Managers)</Typography>
                    <Typography variant="body2" color="text.secondary">How posts are counted on the department dashboards.</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Both off is how the dashboards have always counted. A Manager sets these once Sky has chosen. They
                    change the post counts and metric totals on the department dashboards (Brand, Entertainment, Product,
                    Corporate, Sports). They do not change All products, Post data, or any comment chart.
                  </Typography>
                  <Stack spacing={1}>
                    <DisabledReason reason={switchReason}>
                      <FormControlLabel
                        control={
                          <Switch
                            color="info"
                            checked={posts}
                            disabled={setSettings.isPending}
                            onChange={(_, v) => flip({ postsInSprinklrTable: v, countAsSprinklrTable: v ? asTable : false })}
                          />
                        }
                        label={`Count only posts ${SKY_TABLE} lists`}
                      />
                    </DisabledReason>
                    <Typography variant="caption" color="text.secondary" sx={{ pl: 6 }}>
                      Leaves out posts Platinum holds only because comments point at them, and posts with old numbers that
                      Sky&apos;s table no longer lists. Their comments still count; the dashboards show those posts on their own line.
                    </Typography>
                    <DisabledReason reason={switchReason ?? (!posts ? 'Switch on "Count only posts Sky\'s Sprinklr table lists" first.' : undefined)}>
                      <FormControlLabel
                        control={
                          <Switch
                            color="info"
                            checked={asTable}
                            disabled={setSettings.isPending || !posts}
                            onChange={(_, v) => flip({ postsInSprinklrTable: posts, countAsSprinklrTable: v })}
                          />
                        }
                        label={`Count posts as ${SKY_TABLE} does`}
                      />
                    </DisabledReason>
                    <Typography variant="caption" color="text.secondary" sx={{ pl: 6 }}>
                      Sky&apos;s table shows a post with several programme tags on one row per tag, each with its full reach, so its
                      totals count that post more than once. On, Platinum&apos;s totals do the same and match Sky&apos;s table.
                    </Typography>
                  </Stack>
                  {setSettings.error && <Alert severity="error" sx={{ mt: 1 }}>{(setSettings.error as Error).message}</Alert>}
                  {settings?.updatedBy && settings.updatedAt > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Last changed by {settings.updatedBy}, {utcDateTime(settings.updatedAt)}.
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            </>
          )}
        </Box>
      </Box>
      <CommentDayDrawer
        day={checkDay ? { dayMs: checkDay.dayMs, platform: checkDay.platform, kind: checkDay.kind } : null}
        onClose={() => setCheckDay(null)}
      />
    </Box>
  )
}

export default ReconciliationPage
